import React from 'react';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`;
}

export function formatDate(date: string | Date): string {
  if (!date) return 'N/A';
  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return 'Invalid Date';
  }
}

export function formatDateTime(date: string | Date): string {
  if (!date) return 'N/A';
  try {
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const formattedTime = dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${formattedDate} at ${formattedTime}`;
  } catch (error) {
    return 'Invalid Date';
  }
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

/**
 * Renders text with clickable URLs. Returns an array of React nodes.
 */
export function linkifyText(text: string): (string | React.ReactElement)[] {
  if (!text) return [text];
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
  const parts: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[1];
    parts.push(
      React.createElement('a', {
        key: match.index,
        href: url,
        target: '_blank',
        rel: 'noopener noreferrer',
        className: 'text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 break-all',
      }, url)
    );
    lastIndex = urlRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}


/**
 * Turn a media URL into one that actually SAVES the file.
 * The HTML `download` attribute is IGNORED cross-origin — and our frontend and API are different
 * origins — so a plain <a download> just opened a new tab. Only a Content-Disposition header
 * downloads, so we ask the media proxy for one (?download=1).
 */
export function toDownloadUrl(url?: string, name?: string): string {
  if (!url) return '#';
  if (url.includes('/api/media/proxy/')) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}download=1${name ? `&name=${encodeURIComponent(name)}` : ''}`;
  }
  return url;   // engine-presigned URLs already carry Content-Disposition: attachment
}

// ── Variation Studio (ORBIT) access ──────────────────────────────────────────
// Was a single hardcoded email compared in 6+ components; adding a person meant editing every
// one and missing some. ONE allow-list, checked everywhere.
export const REGEN_ADMIN_EMAILS = ['admin@pearmedia.com', 'rachit@pearmediallc.com'];
export const isRegenAdmin = (email?: string | null): boolean =>
  REGEN_ADMIN_EMAILS.includes((email || '').trim().toLowerCase());

// The real access check for Studio + ORBIT features: the email allow-list OR a per-user
// 'variation_studio' role granted from the admin panel. MIRRORS the backend canUseStudio. Use THIS
// (not isRegenAdmin) for every Studio/ORBIT feature gate so a role-granted user sees them all.
export const canUseStudio = (user?: { email?: string | null; additional_roles?: string[] | null } | null): boolean =>
  isRegenAdmin(user?.email)
  || (Array.isArray(user?.additional_roles) && (user!.additional_roles as string[]).includes('variation_studio'));
